import packageJson from "../../package.json";

// Typed environment accessor for Vite (HashRouter CSR app)
// Blueprint rule: never read import.meta.env outside this module.

export type AppEnv = "dev" | "staging" | "prod" | string;

type EnvShape = {
  VITE_APP_ENV?: string;
  VITE_APP_VERSION?: string;
  VITE_API_BASE_URL?: string;
};

// Internal helper: normalize and guard against undefined.
function readEnv(): EnvShape {
  // `import.meta.env` is Vite-injected; narrow its shape here.
  const env = (
    import.meta as unknown as { env: Record<string, string | undefined> }
  ).env;
  return {
    VITE_APP_ENV: env.VITE_APP_ENV,
    VITE_APP_VERSION: env.VITE_APP_VERSION,
    VITE_API_BASE_URL: env.VITE_API_BASE_URL,
  };
}

const envCache = readEnv();

/**
 * Return the current app environment (dev/staging/prod/other).
 * Defaults to "dev" if unset.
 */
export function getAppEnv(): AppEnv {
  const raw = (envCache.VITE_APP_ENV ?? "").trim().toLowerCase();
  if (raw === "production" || raw === "prod") return "prod";
  if (raw === "staging" || raw === "stg") return "staging";
  if (raw === "development" || raw === "dev") return "dev";
  return raw || "dev";
}

/**
 * Return the app version, preferring an explicit env override; otherwise falls back to package.json.
 */
export function getAppVersion(): string {
  const envVersion = (envCache.VITE_APP_VERSION ?? "").trim();
  if (envVersion) return envVersion;
  return packageJson.version || "0.0.0";
}

/**
 * Return the configured API base URL (may be undefined in mock mode).
 */
export function getApiBaseUrl(): string | undefined {
  const val = envCache.VITE_API_BASE_URL?.trim();
  if (val) {
    return val.endsWith("/admin") ? val : `${val.replace(/\/$/, "")}/admin`;
  }

  const env = getAppEnv();
  if (env === "dev") {
    return "http://localhost:3000/admin";
  }
  return "https://payroll-bffe.demo.kamukirim.com/admin";
}

/**
 * Safe accessor to retrieve an env value by key (prefixed only).
 */
export function getEnv(key: keyof EnvShape): string | undefined {
  return envCache[key]?.trim() || undefined;
}
